import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaTag, FaTimes, FaIndustry, 
  FaBox, FaCalendarAlt, FaShieldAlt, FaVial,
  FaShoppingCart, FaBolt 
} from "react-icons/fa";

const MedicineDetailPopup = ({ medicine, onClose, handleAddToCart, orderNow }) => {
  const [quantity, setQuantity] = useState(1);

  if (!medicine) return null;

  const isAvailable = medicine.totalPiece > 0 && !medicine.isDiscontinued;

  const onQtyChange = (delta) => {
    setQuantity(prev => Math.max(prev + delta, 1));
  };

  return (
    <AnimatePresence>
      <div style={{ zIndex: 3000 }}>
        {/* Dark Overlay with Blur */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={styles.overlay}
        />

        {/* Sidebar Panel */}
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          style={styles.sidebar}
        >
          {/* Header */}
          <div style={styles.header}>
            <h2 style={styles.headerTitle}>Medicine Details</h2>
            <button style={styles.closeBtn} onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          <div style={styles.content}>
            <div style={styles.imageContainer}>
              <img
                src={medicine.images?.[0] || "/para.png"}
                alt={medicine.name}
                style={styles.image}
                onError={(e) => { e.target.onerror = null; e.target.src = "/para.png"; }}
              />
            </div>

            <div style={styles.mainInfo}>
              <h1 style={styles.name}>{medicine.name}</h1>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span style={styles.categoryBadge}>{medicine.type}</span>
                {!isAvailable && (
                    <span style={styles.outOfStockBadge}>Out of Stock</span>
                )}
              </div>
            </div>

            <hr style={styles.divider} />

            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <FaTag style={styles.icon} />
                <div>
                  <p style={styles.label}>Price</p>
                  <p style={styles.value}>₹{(medicine.discountedPrice * quantity).toFixed(2)}</p>
                </div>
              </div>
              <div style={styles.infoItem}>
                <FaIndustry style={styles.icon} />
                <div>
                  <p style={styles.label}>Manufacturer</p>
                  <p style={styles.value}>{medicine.manufacturer}</p>
                </div>
              </div>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}><FaShieldAlt /> About Medication</h3>
              <p style={styles.descriptionText}>{medicine.description}</p>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}><FaVial /> Composition</h3>
              <div style={styles.compositionBox}>
                <p><strong>Primary:</strong> {medicine.comp1}</p>
                <p><strong>Secondary:</strong> {medicine.comp2}</p>
              </div>
            </div>
          </div>

          {/* Sticky Footer Actions */}
          <div style={styles.footer}>
            <div style={styles.qtyWrapper}>
              <button disabled={!isAvailable} onClick={() => onQtyChange(-1)} style={styles.qtyBtn}>-</button>
              <span style={styles.qtyText}>{quantity}</span>
              <button disabled={!isAvailable} onClick={() => onQtyChange(1)} style={styles.qtyBtn}>+</button>
            </div>

            <div style={styles.btnGroup}>
              <motion.button 
                whileHover={isAvailable ? { scale: 1.02 } : {}}
                whileTap={isAvailable ? { scale: 0.98 } : {}}
                disabled={!isAvailable}
                onClick={() => handleAddToCart(medicine, quantity)} 
                style={{ ...styles.actionBtn, ...styles.cartBtn, opacity: isAvailable ? 1 : 0.6 }}
              >
                <FaShoppingCart /> Add to Cart
              </motion.button>
              
              <motion.button 
                whileHover={isAvailable ? { scale: 1.02 } : {}}
                whileTap={isAvailable ? { scale: 0.98 } : {}}
                disabled={!isAvailable}
                onClick={() => orderNow(medicine, quantity)} 
                style={{ ...styles.actionBtn, ...styles.buyBtn, opacity: isAvailable ? 1 : 0.6 }}
              >
                <FaBolt /> Buy Now
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const styles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)", zIndex: 3000 },
 sidebar: {
  position: "fixed",
  top: 0,
  right: 0,
  width: "100%",
  maxWidth: "450px",
  height: "100%",
  backgroundColor: "#fff",
  zIndex: 3000, // Increased to be above almost everything
  display: "flex",
  flexDirection: "column",
  boxShadow: "-10px 0 30px rgba(0,0,0,0.1)",
},
  header: { padding: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f1f5f9" },
  headerTitle: { fontSize: "1.1rem", fontWeight: "700", color: "#1e293b", margin: 0 },
  closeBtn: { background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer", color: "#94a3b8" },
  content: { flex: 1, overflowY: "auto", padding: "20px" },
  imageContainer: { width: "100%", height: "180px", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "#f8fafc", borderRadius: "12px", marginBottom: "20px" },
  image: { maxHeight: "140px", objectFit: "contain" },
  name: { fontSize: "1.6rem", fontWeight: "800", color: "#0f172a", marginBottom: "10px", lineHeight: "1.2" },
  categoryBadge: { backgroundColor: "#e0f2fe", color: "#0369a1", padding: "4px 12px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: "600" },
  outOfStockBadge: { backgroundColor: "#fee2e2", color: "#b91c1c", padding: "4px 12px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: "600" },
  divider: { border: 0, borderTop: "1px solid #f1f5f9", margin: "20px 0" },
  infoGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "20px" },
  infoItem: { display: "flex", alignItems: "center", gap: "10px" },
  icon: { color: "#3b82f6", fontSize: "1.1rem" },
  label: { fontSize: "0.7rem", color: "#94a3b8", margin: 0, textTransform: "uppercase", fontWeight: "700" },
  value: { fontSize: "0.9rem", color: "#334155", margin: 0, fontWeight: "700" },
  section: { marginBottom: "20px" },
  sectionTitle: { fontSize: "0.95rem", fontWeight: "700", color: "#1e293b", display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" },
  compositionBox: { backgroundColor: "#f8fafc", padding: "12px", borderRadius: "8px", fontSize: "0.85rem", color: "#475569" },
  descriptionText: { fontSize: "0.9rem", color: "#64748b", lineHeight: "1.6" },
  footer: { padding: "20px", borderTop: "1px solid #f1f5f9", backgroundColor: "#fff" },
  qtyWrapper: { display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", marginBottom: "15px" },
  qtyBtn: { width: "35px", height: "35px", borderRadius: "50%", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: "1.2rem" },
  qtyText: { fontSize: "1.2rem", fontWeight: "700" },
  btnGroup: { display: "flex", gap: "10px" },
  actionBtn: { flex: 1, padding: "14px", borderRadius: "10px", border: "none", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  cartBtn: { backgroundColor: "#f1f5f9", color: "#0f172a" },
  buyBtn: { backgroundColor: "#0d6efd", color: "#fff" }
};

export default MedicineDetailPopup;